import { useCallback, useRef, useState } from 'react'
import styled from 'styled-components'

import { IconArrowDown } from 'components/Icons/IconArrowDown'
import { IconCheck } from 'components/Icons/IconCheck'
import { Typography } from 'components/Typography/Typography'
import { useOnClickOutsideMultiple } from 'utils/onClickOutside'
import { Menu } from 'components/Menu/MenuWithPortal'
import { WITHDRAW_DIALOG_ID } from '../WithdrawDialog'
import { SwapType } from '../hooks/useWithdrawDetails'

const MenuTrigger = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 10px;
  border-radius: 8px;
  border: 0.7px solid ${p => p.theme.colors.divider};
  cursor: pointer;
  div {
    display: flex;
    align-items: center;
  }
`

const StyledMenuItem = styled.div`
  & > div {
    padding: 8px 12px;
    display: flex;
    align-items: center;
    cursor: pointer;
    &:hover {
      background-color: ${p => p.theme.colors.background.hover};
    }
  }
`

interface MenuItemProps {
  item: SwapType
  activeOption: SwapType
  onClick: (o: SwapType) => void
}

const MenuItem = ({ item, onClick, activeOption }: MenuItemProps) => (
  <StyledMenuItem onClick={() => onClick(item)}>
    <Typography variant="caption" fontWeight="bold">
      {SwapTypeLabels[item]}
      {item === activeOption && (
        <div style={{ marginLeft: 'auto', display: 'flex' }}>
          <IconCheck />
        </div>
      )}
    </Typography>
  </StyledMenuItem>
)

const SwapTypeLabels = {
  'exact-in': 'Exact in',
  'exact-out': 'Exact out',
}

interface Props {
  swapType: SwapType
  handleSwapTypeChange: (swapType: SwapType) => void
}

export const SwapTypeMenu = ({ swapType, handleSwapTypeChange }: Props) => {
  const [isMenuOpen, setMenuOpen] = useState(false)

  const menuTriggerRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  const onClose = useCallback(() => setMenuOpen(false), [])

  useOnClickOutsideMultiple([menuTriggerRef, menuRef], onClose)

  const toggleMenu = useCallback(() => setMenuOpen(!isMenuOpen), [isMenuOpen])

  const handleOptionClick = useCallback(
    (newSwapType: SwapType) => {
      handleSwapTypeChange(newSwapType)
      toggleMenu()
    },
    [handleSwapTypeChange, toggleMenu]
  )

  return (
    <div style={{ position: 'relative', marginTop: 8 }}>
      <MenuTrigger onClick={toggleMenu} ref={menuTriggerRef}>
        <Typography variant="caption" fontWeight="bold">
          {SwapTypeLabels[swapType]}
        </Typography>
        <div>
          <IconArrowDown />
        </div>
      </MenuTrigger>
      {isMenuOpen && (
        <Menu ref={menuRef} anchorElement={menuTriggerRef} scrollContainerId={WITHDRAW_DIALOG_ID}>
          {['exact-in', 'exact-out'].map(o => (
            <MenuItem key={o} item={o as SwapType} onClick={handleOptionClick} activeOption={swapType} />
          ))}
        </Menu>
      )}
    </div>
  )
}
